import { useQuery } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useAuth } from "./auth";

// Phase 6.12.1 - Owner identity resolution (+ 6.12.2: preferred_locale).
// Mirrors useTenantIdentity() for the owner user class. A "linked owner" is an
// owner_clients row whose user_id = the current auth user. Complements the
// current_owner_client_id() SQL helper (migration 016); RLS enforces the real
// boundary, this hook drives routing/UI and supplies the language preference.

export interface OwnerIdentity {
  id: string;
  organization_id: string;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  preferred_locale: string;
}

/**
 * Resolves the current auth user's linked owner_clients record, if any.
 * Returns null (not an error) when the user is not an owner.
 */
export function useOwnerIdentity() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["owner-identity", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<OwnerIdentity | null> => {
      const { data, error } = await supabase
        .from("owner_clients")
        .select("id, organization_id, display_name, first_name, last_name, preferred_locale")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return (data as OwnerIdentity | null) ?? null;
    },
  });
}
